import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Manage fullscreen state for an interactive fractal canvas container,
 * keeping the view in full-viewport mode across renderer transitions and
 * exiting when Escape is pressed.
 */
export function useFullscreenControl({
  isFullscreen: controlledFullscreen,
  onFullscreenChange,
} = {}) {
  const containerRef = useRef(null);
  const [uncontrolledFullscreen, setUncontrolledFullscreen] = useState(false);
  const isFullscreen = Boolean(controlledFullscreen ?? uncontrolledFullscreen);
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;

  const setFullscreen = useCallback(next => {
    const value = typeof next === 'function'
      ? Boolean(next(isFullscreenRef.current))
      : Boolean(next);
    isFullscreenRef.current = value;
    if (typeof document !== 'undefined') {
      document.body?.classList.toggle('fullscreen-active', value);
    }
    if (containerRef.current) {
      containerRef.current.classList.toggle('fullscreen', value);
      containerRef.current.setAttribute('data-fullscreen', value ? 'true' : 'false');
    }
    if (controlledFullscreen === undefined) {
      setUncontrolledFullscreen(value);
    }
    onFullscreenChange?.(value);
  }, [controlledFullscreen, onFullscreenChange]);

  const enterFullscreen = useCallback(() => {
    setFullscreen(true);
  }, [setFullscreen]);

  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
  }, [setFullscreen]);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreenRef.current) exitFullscreen();
    else enterFullscreen();
  }, [enterFullscreen, exitFullscreen]);

  useLayoutEffect(() => {
    if (!isFullscreen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        exitFullscreen();
      }
    };

    document.body?.classList.add('fullscreen-active');
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.body?.classList.remove('fullscreen-active');
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isFullscreen, exitFullscreen]);

  return {
    containerRef,
    isFullscreen,
    isFullscreenRef,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
}

export function FullscreenButton({ isFullscreen, onToggle }) {
  const label = isFullscreen ? 'Exit fullscreen' : 'Fullscreen';
  return (
    <button
      type="button"
      className="fullscreen-button"
      aria-label={label}
      aria-pressed={isFullscreen}
      title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
      onClick={event => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <svg
        className="fullscreen-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        aria-hidden="true"
        focusable="false"
      >
        {isFullscreen ? (
          <path
            fill="currentColor"
            d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
          />
        ) : (
          <path
            fill="currentColor"
            d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
          />
        )}
      </svg>
    </button>
  );
}
